import { useCallback, useMemo, useState } from "react";

import { executeManagingPrompt } from "../api/ChatApi";
import {
  buildCallRecordingExecuteContext,
  CHAT_PROMPT_CUSTOM_TYPE,
  promptRequiresCallRecording,
} from "../constants/chatConstants";
import type {
  ChatMessage,
  CrmChatRecordContext,
  ExecuteManagingPromptBody,
  ManagingPromptCatalogEntry,
} from "../types/chat.types";

interface SendOptions {
  /** Free-text from the composer. May be empty for one-shot prompt cards. */
  content?: string;
  /** Prompt to execute; falls back to `custom` when only `content` is given. */
  prompt?: ManagingPromptCatalogEntry | null;
  /** Optional extra context (transcript snippets, issue keys, etc). */
  context?: Record<string, unknown>;
  /** Activity id for transcript-dependent prompts (merged into context). */
  callRecordingActivityId?: string | null;
}

interface UseChatSendArgs {
  /** When false, returns inert stubs (agent loop hook owns messaging). */
  active?: boolean;
  workspaceId: string;
  record?: CrmChatRecordContext | null;
  /** Selected call/meeting activity for transcript prompts. */
  callRecordingActivityId?: string | null;
  /** Optional callback to mirror messages into a persistent store. */
  onMessagesChange?: (messages: ChatMessage[]) => void;
  initialMessages?: ChatMessage[];
}

interface UseChatSendResult {
  messages: ChatMessage[];
  send: (opts: SendOptions) => Promise<void>;
  isSending: boolean;
  error: string | null;
  reset: () => void;
  regenerate: (assistantMessageId: string) => Promise<void>;
}

function makeId(prefix: string): string {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function formatUserSummary(opts: SendOptions): string {
  const parts: string[] = [];
  if (opts.prompt && opts.prompt.promptType !== CHAT_PROMPT_CUSTOM_TYPE) {
    parts.push(opts.prompt.name);
  }
  if (opts.content?.trim()) {
    parts.push(opts.content.trim());
  }
  return parts.join(" — ");
}

/**
 * Phase-1 send hook: executes the chosen managing prompt against the current
 * record and appends the result as an assistant message. The local thread
 * lives in component state — phase 2 will persist it via the chat sessions
 * API.
 */
const INACTIVE_SEND: UseChatSendResult = {
  messages: [],
  send: async () => {},
  isSending: false,
  error: null,
  reset: () => {},
  regenerate: async () => {},
};

export function useChatSend({
  active = true,
  workspaceId,
  record,
  callRecordingActivityId,
  onMessagesChange,
  initialMessages,
}: UseChatSendArgs): UseChatSendResult {
  const [messages, setMessages] = useState<ChatMessage[]>(initialMessages ?? []);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pushMessages = useCallback(
    (next: ChatMessage[] | ((prev: ChatMessage[]) => ChatMessage[])) => {
      setMessages((prev) => {
        const resolved = typeof next === "function" ? next(prev) : next;
        onMessagesChange?.(resolved);
        return resolved;
      });
    },
    [onMessagesChange]
  );

  const reset = useCallback(() => {
    pushMessages([]);
    setError(null);
  }, [pushMessages]);

  const runExecute = useCallback(
    async (opts: SendOptions): Promise<void> => {
      if (!active) return;
      const promptType = opts.prompt?.promptType ?? CHAT_PROMPT_CUSTOM_TYPE;
      const isCustom = promptType === CHAT_PROMPT_CUSTOM_TYPE;
      const trimmedContent = opts.content?.trim() ?? "";

      if (isCustom && !trimmedContent) {
        setError("Please type a message before sending.");
        return;
      }

      const promptNeedsTranscript =
        !isCustom && opts.prompt != null && promptRequiresCallRecording(opts.prompt);
      if (promptNeedsTranscript && !opts.callRecordingActivityId) {
        setError("Select a call or meeting with a transcript before running this prompt.");
        return;
      }

      const context: Record<string, unknown> = { ...(opts.context ?? {}) };
      if (opts.callRecordingActivityId) {
        Object.assign(context, buildCallRecordingExecuteContext(opts.callRecordingActivityId));
      }

      const body: ExecuteManagingPromptBody = {
        recordId: record?.recordId,
        context: Object.keys(context).length > 0 ? context : undefined,
        language: "en",
      };
      if (isCustom) {
        body.customPrompt = trimmedContent;
      } else if (trimmedContent) {
        body.customPrompt = trimmedContent;
      }

      const userSummary = formatUserSummary(opts) || opts.prompt?.name || "Custom prompt";
      const now = new Date().toISOString();
      const sessionId = "local";

      const userMessage: ChatMessage = {
        id: makeId("user"),
        sessionId,
        role: "user",
        content: userSummary,
        createdAt: now,
        promptType: opts.prompt?.promptType,
        promptName: opts.prompt?.name,
      };

      pushMessages((prev) => [...prev, userMessage]);
      setError(null);
      setIsSending(true);

      try {
        const result = await executeManagingPrompt(workspaceId, promptType, body);
        const assistant: ChatMessage = {
          id: makeId("assistant"),
          sessionId,
          role: "assistant",
          content: result.value,
          contentDelta: result.contentDelta,
          actions: result.actions?.length ? result.actions : undefined,
          createdAt: new Date().toISOString(),
          promptType: result.promptType,
          promptName: opts.prompt?.name,
          metadata: {
            ...result.metadata,
            contextSummary: record
              ? {
                  recordId: record.recordId,
                  recordType: record.recordType,
                }
              : undefined,
          },
        };
        pushMessages((prev) => [...prev, assistant]);
      } catch (err) {
        const errData = (err as { response?: { data?: { message?: string; error?: string } } })
          ?.response?.data;
        const message =
          errData?.message ??
          errData?.error ??
          (err as Error)?.message ??
          "Something went wrong while generating a response.";
        setError(message);
        const systemMessage: ChatMessage = {
          id: makeId("error"),
          sessionId,
          role: "system",
          content: message,
          createdAt: new Date().toISOString(),
          error: message,
        };
        pushMessages((prev) => [...prev, systemMessage]);
      } finally {
        setIsSending(false);
      }
    },
    [active, callRecordingActivityId, pushMessages, record, workspaceId]
  );

  const regenerate = useCallback(
    async (assistantMessageId: string) => {
      if (!active) return;
      const idx = messages.findIndex((m) => m.id === assistantMessageId);
      if (idx <= 0) return;
      const priorUser = [...messages.slice(0, idx)].reverse().find((m) => m.role === "user");
      if (!priorUser) return;
      pushMessages((prev) => prev.slice(0, idx));
      await runExecute({
        content: priorUser.promptName ? undefined : priorUser.content,
        prompt: priorUser.promptType
          ? {
              promptType: priorUser.promptType,
              name: priorUser.promptName ?? priorUser.promptType,
              description: "",
              categories: [],
            }
          : null,
        callRecordingActivityId,
      });
    },
    [active, callRecordingActivityId, messages, pushMessages, runExecute]
  );

  return useMemo(
    () =>
      active
        ? { messages, send: runExecute, isSending, error, reset, regenerate }
        : INACTIVE_SEND,
    [active, messages, runExecute, isSending, error, reset, regenerate]
  );
}
